import {CAROUSEL_SLIDES, type ContentPreviewDto} from '@devmil/contracts';
import {useEffect, useState} from 'react';

/**
 * A SEQUÊNCIA do carrossel, na tela onde o dono decide.
 *
 * ## O que o plano pede, e por que cada peça está aqui
 *
 * "O painel deve permitir visualizar os slides na ordem, ampliar, conferir
 * legenda e pareceres, pedir ajustes e aprovar o conjunto exato."
 *
 * - **Na ordem**, com o contador `3 / 7` e as setas. Um carrossel aprovado sem
 *   olhar a ordem é uma peça aprovada pela metade: a ordem é justamente o que
 *   distingue sete imagens soltas de uma sequência, e é ela que o manifesto
 *   sela.
 * - **Ampliar**, porque o quadro da coluna de revisão tem 170 px de largura e o
 *   texto do slide é desenhado para 1080. Ler "a dica cabe no slide?" num
 *   quadro de miniatura é não ler.
 * - **Miniaturas**, para o dono ver a sequência INTEIRA de uma vez. Sem elas, a
 *   única forma de saber se o slide 4 repete o 2 é clicar sete vezes e lembrar.
 * - **Pedir ajuste indicando o slide**, pelo fluxo que já existe: o botão
 *   preenche o começo do comentário com "Slide 4: " e abre a caixa de sempre.
 *   Um segundo fluxo de pedido de alteração seria uma segunda régua de
 *   comentário, uma segunda rota e uma segunda chance de divergir.
 * - **O estado do CONJUNTO**, com todas as letras. Uma peça que renderizou
 *   cinco de sete slides não é uma peça com cinco slides: é uma peça
 *   incompleta, e o dono precisa ver isso ANTES de aprovar, e não depois, na
 *   recusa do portão.
 *
 * ## O que ele NÃO faz
 *
 * Não aprova slide. A aprovação é do CONJUNTO, uma só, ligada ao manifesto que
 * sela os sete: um botão por slide sugeriria que dá para liberar seis, e não
 * dá — a publicação forma o contêiner pai só com os sete filhos prontos.
 */

/** Os endereços dos slides, já montados pela porta de dados. */
export interface SlideDoPainel {
  position: number;
  /** `null` quando a fonte não tem endereço para dar (a simulada nunca tem). */
  url: string | null;
}

export interface CarouselViewerProps {
  /** Quantos slides o servidor MEDIU. Zero é peça ainda não renderizada. */
  slides: readonly SlideDoPainel[];
  /** O esquema, desenhado quando não há arquivo para mostrar. */
  preview: ContentPreviewDto;
  /** Chamado quando o dono pede ajuste NUM slide. */
  onPedirAjuste: (posicao: number) => void;
  /** Trocar de peça volta para o slide 1. */
  contentVersionId: string;
}

export const CarouselViewer = ({
  slides,
  preview,
  onPedirAjuste,
  contentVersionId,
}: CarouselViewerProps) => {
  const [atual, setAtual] = useState(0);
  const [ampliado, setAmpliado] = useState(false);

  /*
   * Trocar de peça volta ao slide 1 e fecha a ampliação.
   *
   * Sem isto, quem estivesse no slide 6 de um carrossel e clicasse no seguinte
   * cairia no slide 6 do outro, e a ampliação aberta mostraria o slide de uma
   * peça com a legenda de outra. É o mesmo motivo da `key` no quadro do Reel.
   */
  useEffect(() => {
    setAtual(0);
    setAmpliado(false);
  }, [contentVersionId]);

  const total = slides.length;
  const completo = total === CAROUSEL_SLIDES;
  const slide = slides[Math.min(atual, Math.max(0, total - 1))];

  const ir = (destino: number) => {
    if (total === 0) {
      return;
    }
    // Circular: do 7 volta para o 1. É como o Instagram não faz, e é como a
    // conferência precisa ser — quem está no fim e quer rever a capa não deve
    // clicar seis vezes para trás.
    setAtual(((destino % total) + total) % total);
  };

  if (total === 0) {
    return (
      <div className="dm-reel-box">
        <div
          className="dm-reel"
          aria-label="Este carrossel ainda não foi renderizado"
        >
          <span className="dm-reel-brand">{preview.brand}</span>
          <strong className="dm-reel-hook">{preview.hook}</strong>
          <span className="dm-reel-foot">sem slides</span>
        </div>
        <p className="dm-carousel-state" role="status">
          {`Nenhum slide renderizado. O carrossel tem ${CAROUSEL_SLIDES} slides e não pode ser aprovado assim.`}
        </p>
      </div>
    );
  }

  const posicao = slide!.position;

  return (
    <div className="dm-reel-box">
      <div
        className="dm-carousel-frame"
        aria-label={`Slide ${posicao} de ${total} do carrossel`}
      >
        {slide!.url === null ? (
          /*
           * Sem endereço, o esquema. Ele é o mesmo quadro do Reel em rascunho,
           * e não um retângulo vazio: enquanto a peça não foi renderizada não
           * existe slide nenhum para mostrar, e um quadro vazio seria a tela de
           * aprovação piorando justamente no dia em que a renderização falha.
           */
          <div className="dm-reel dm-carousel-esquema">
            <span className="dm-reel-brand">{preview.brand}</span>
            <strong className="dm-reel-hook">{preview.hook}</strong>
            <span className="dm-reel-foot">{`${posicao} / ${total}`}</span>
          </div>
        ) : (
          <img
            className="dm-carousel-media"
            src={slide!.url}
            alt={`Slide ${posicao} do carrossel, renderizado`}
          />
        )}
      </div>

      <div className="dm-carousel-nav">
        <button
          type="button"
          className="dm-carousel-arrow"
          onClick={() => {
            ir(atual - 1);
          }}
          aria-label="Slide anterior"
        >
          {'‹'}
        </button>
        <span className="dm-carousel-count" aria-live="polite">
          {`${posicao} / ${total}`}
        </span>
        <button
          type="button"
          className="dm-carousel-arrow"
          onClick={() => {
            ir(atual + 1);
          }}
          aria-label="Próximo slide"
        >
          {'›'}
        </button>
      </div>

      <ul className="dm-carousel-thumbs">
        {slides.map((miniatura, indice) => (
          <li key={miniatura.position}>
            <button
              type="button"
              className="dm-carousel-thumb"
              aria-label={`Ver o slide ${miniatura.position}`}
              aria-pressed={indice === atual}
              onClick={() => {
                setAtual(indice);
              }}
            >
              {miniatura.url === null ? (
                miniatura.position
              ) : (
                <img src={miniatura.url} alt="" />
              )}
            </button>
          </li>
        ))}
      </ul>

      <div className="dm-carousel-actions">
        <button
          type="button"
          className="dm-reel-play"
          aria-expanded={ampliado}
          onClick={() => {
            setAmpliado((antes) => !antes);
          }}
        >
          {ampliado ? 'Fechar ampliação' : 'Ampliar slide'}
        </button>
        <button
          type="button"
          className="dm-carousel-adjust"
          onClick={() => {
            onPedirAjuste(posicao);
          }}
        >
          {`Pedir ajuste no slide ${posicao}`}
        </button>
      </div>

      {/*
        O ESTADO DO CONJUNTO, e ele fala mesmo quando está tudo certo.
        Uma linha que só aparecesse no erro faria "sem aviso" significar duas
        coisas: peça completa e tela que esqueceu de conferir.
      */}
      <p
        className={
          completo ? 'dm-carousel-state' : 'dm-carousel-state dm-carousel-warn'
        }
        role="status"
      >
        {completo
          ? `${CAROUSEL_SLIDES} de ${CAROUSEL_SLIDES} slides renderizados, na ordem.`
          : `Só ${total} de ${CAROUSEL_SLIDES} slides renderizados. A peça está incompleta.`}
      </p>

      {ampliado && slide!.url !== null ? (
        <div className="dm-carousel-zoom">
          <img
            src={slide!.url}
            alt={`Slide ${posicao} do carrossel, ampliado`}
          />
        </div>
      ) : null}
    </div>
  );
};
